import useSafeState from "../utils/safe-state";
import {Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";
import TextArea from "antd/es/input/TextArea";
import ItemDataForm from "./item-data-form";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const ItemDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);

    const [supplierOptions, setSupplierOptions] = useSafeState<any[]>([]);
    const [supplierLoading, setSupplierLoading] = useSafeState(false);
    const [isSpare, setIsSpare] = useState(false);
    const [userSupplierCode, setUserSupplierCode] = useState<any>();
    
    const [step, setStep] = useState(0);
    const [itemFormValues, setItemFormValues] = useState<any>(null);


    useEffect(() => {

        setUserSupplierCode(localStorage.getItem('supplierCode'));
        form.resetFields();

        if(localStorage.getItem('supplierCode') == 'undefined'){
            getSuppliers();
        }

        if(props.mode === 'Edit') {

            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if(key === 'productionStartDate' || key === 'productionEndDate') {
                    fieldsValues.push({name: key, value: value ? moment(value as string) : null});
                } else {
                    fieldsValues.push({name: key, value: value});
                }
                console.log(key, value);
                if(key === 'isSparePart'){
                    setIsSpare(value == true);
                }
            }
            form.setFields(fieldsValues);
        }

    }, []);

    const getSuppliers = () => {

        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/suppliers')
                .then(response => {
                    const suppliers = Array.isArray(response.data) ? response.data : [];
                    setSupplierOptions(suppliers);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const onReset = () => {
        form.resetFields();
        setIsSpare(false)
    };

    const handleFinish = (values: any) => {

        const formattedValues = {
            id: values.id || form.getFieldValue(['id']) || data.id,
            itemNo: values.itemNo,
            name: values.name,
            tarricCode: values.tarricCode,
            tradeName: values.tradeName,
            relevantProducts: values.relevantProducts,
            scientificName: values.scientificName,
            weight: values.weight,
            countryAndRegionOfProduction: values.countryAndRegionOfProduction,
            productionStartDate: values.productionStartDate ? moment(values.productionStartDate).format('YYYY-MM-DD HH:mm') : null,
            productionEndDate: values.productionEndDate ? moment(values.productionEndDate).format('YYYY-MM-DD HH:mm') : null,
            isSparePart: values.isSparePart,
            version: form.getFieldValue(['version']),
            supplier: {
                code: userSupplierCode == 'undefined'
                    ? (values.supplier && values.supplier.code) || form.getFieldValue(['supplier', 'code'])
                    : userSupplierCode
            }
        };

        setItemFormValues(formattedValues);
        setStep(1);
    }

    if (step === 1) {
        return (
            <ItemDataForm
                data={{ itemId: data.id, itemFormValues: itemFormValues }}
                onClose={() => {
                    if (props.onClose) {
                        props.onClose();
                    }
                }}
                onBack={() => setStep(0)}
            />
        );
    }

    return (
        <Form
            id="newItem"
            layout='vertical'
            form={form}
            onFinish={handleFinish}
        >
            <Spin spinning={loading || supplierLoading}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="itemNo"
                            label={<Trans>Item No</Trans>}
                            rules={[{required: true, message: <Trans>Please enter the item number</Trans>}]}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="tarricCode"
                            label={<Trans>Tarric Code</Trans>}
                            rules={[{required: true, message: <Trans>Please enter the Tarric code</Trans>}]}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label={<Trans>Name</Trans>}
                            rules={[{required: true, message: <Trans>Please enter the name</Trans>}]}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={['supplier', 'code']}
                            label={<Trans>Supplier</Trans>}
                            hidden={userSupplierCode != 'undefined'}
                            rules={[{
                                required: userSupplierCode == 'undefined',
                                message: <Trans>Please select the supplier</Trans>
                            }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select supplier</Trans>}
                                optionFilterProp="children"
                            >
                                {supplierOptions.map(supplier => (
                                    <Select.Option key={supplier.code} value={supplier.code}>
                                        {supplier.code} - {supplier.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="tradeName"
                            label={<Trans>Trade Name</Trans>}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="isSparePart"
                            valuePropName="checked"
                        >
                            <Checkbox><Trans>Is Spare Part</Trans></Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="relevantProducts"
                            label={<Trans>Relevant Products</Trans>}
                        >
                            <TextArea rows={3}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="scientificName"
                            label={<Trans>Scientific Name</Trans>}
                        >
                            <TextArea rows={3}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="weight"
                            label={<Trans>Weight</Trans>}
                        >
                            <InputNumber min={0} style={{width: '100%'}}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="productionStartDate"
                            label={<Trans>Production Start Date</Trans>}
                        >
                            <DatePicker style={{width: '100%'}} showTime format="YYYY-MM-DD HH:mm"/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="productionEndDate"
                            label={<Trans>Production End Date</Trans>}
                        >
                            <DatePicker style={{width: '100%'}} showTime format="YYYY-MM-DD HH:mm"/>
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    right: 0,
                    width: 720,
                    background: "#fff",
                    borderTop: "1px solid #e8e8e8",
                    padding: "10px 20px",
                    zIndex: 100,
                    textAlign: "right"
                }}
            >
                <Button htmlType="button" style={{marginRight: 8}} onClick={() => onReset()}>
                    <Trans>Clear</Trans>
                </Button>
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Next</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default ItemDrawer;
